import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { AlertWarningCircleIcon, ChatBubbleSquareIcon, MailSendEnvelopeIcon } from '@calendly/ui/components/icons';
import { Group } from '@calendly/ui/components/menu';
import { ProTip } from '@calendly/ui/components/pro-tip';
import { TitleText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { isOnFreeStageSelector } from 'features/billing';
import { useEventType } from 'features/event_type_provider';
import { useIsSmsDeactivated } from 'features/organization_settings';
import { useIsNotificationCustomizationRestricted } from 'hooks/useIsNotificationCustomizationRestricted';
import { isAnyLocksEnabled } from 'services/event_type_locking';
import { BillingWrappedActivationToggleItem } from './billing_wrapped_activation_toggle_item';
import { EditItem } from './styles';
import { buildSectionHandler, getNotificationTypeCards, getTextReminderSubtitle, getTimingsDescription, sectionTitles } from './utils';
import { useSelectedSectionsActions } from '../../components/selected_sections';
import { StackedCards } from '../components';
const labelContainerCss = "l14ch1ef";
const LabelContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(labelContainerCss, props.className)} />;
});
export const BasicNotificationsSection = ({
  onSubmit,
  doNotSlideBackOnToggleUpdate = false
}) => {
  const eventType = useEventType();
  const {
    selectSection
  } = useSelectedSectionsActions();
  const isSmsDeactivated = useIsSmsDeactivated();
  const isOnFreeStage = useSelector(isOnFreeStageSelector);
  const isNotificationCustomizationRestricted = useIsNotificationCustomizationRestricted();
  const emailReminderActivated = !!eventType.custom_notifications?.invitee_reminder?.enabled && !isOnFreeStage;
  const textReminderActivated = !!eventType.custom_notifications?.invitee_sms_reminder?.enabled && !isOnFreeStage;
  const emailFollowUpActivated = !!eventType.custom_notifications?.invitee_email_follow_up?.enabled && !isOnFreeStage;
  const etLocked = !eventType.isMETParent && isAnyLocksEnabled(eventType, ['v3_notifications']);
  const notificationTypeCards = getNotificationTypeCards(eventType.notification_type, selectSection, etLocked);
  const emailRemindersTitle = sectionTitles.emailReminders();
  const textRemindersTitle = sectionTitles.textReminders();
  const emailFollowUpTitle = sectionTitles.emailFollowUp();
  const emailReminderHandler = buildSectionHandler('email_reminders', emailRemindersTitle, selectSection);
  const textReminderHandler = buildSectionHandler('text_reminders', textRemindersTitle, selectSection);
  const emailFollowUpHandler = buildSectionHandler('email_follow_up', emailFollowUpTitle, selectSection);
  const cards = [...notificationTypeCards, {
    icon: <MailSendEnvelopeIcon size="large" />,
    htmlId: 'email-reminders',
    readOnly: etLocked,
    sectionHandler: emailReminderHandler,
    title: emailRemindersTitle,
    badgeText: !emailReminderActivated && I18n.t('publisher.action.off'),
    subtitle: emailReminderActivated && getTimingsDescription({
      eventType,
      notificationName: 'invitee_reminder'
    }),
    menuItems: <>
          <Group>
            <EditItem onClick={emailReminderHandler} />
          </Group>
          <Group>
            <BillingWrappedActivationToggleItem onSubmit={onSubmit} active={emailReminderActivated} kind="invitee_reminder" doNotSlideBackOnUpdate={doNotSlideBackOnToggleUpdate} />
          </Group>
        </>
  }, {
    icon: <ChatBubbleSquareIcon size="large" />,
    htmlId: 'text-reminders',
    readOnly: etLocked,
    disabled: !!isSmsDeactivated,
    sectionHandler: textReminderHandler,
    title: textRemindersTitle,
    subtitle: getTextReminderSubtitle(isSmsDeactivated, textReminderActivated, eventType),
    badgeText: !isSmsDeactivated && !textReminderActivated && I18n.t('publisher.action.off'),
    menuItems: !isSmsDeactivated && <>
          <Group>
            <EditItem onClick={textReminderHandler} />
          </Group>
          <Group>
            <BillingWrappedActivationToggleItem onSubmit={onSubmit} active={textReminderActivated} kind="invitee_sms_reminder" doNotSlideBackOnUpdate={doNotSlideBackOnToggleUpdate} />
          </Group>
        </>
  }, {
    icon: <MailSendEnvelopeIcon size="large" />,
    htmlId: 'email-follow-up',
    readOnly: etLocked,
    sectionHandler: emailFollowUpHandler,
    title: emailFollowUpTitle,
    subtitle: emailFollowUpActivated && getTimingsDescription({
      eventType,
      notificationName: 'invitee_email_follow_up'
    }),
    badgeText: !emailFollowUpActivated && I18n.t('publisher.action.off'),
    menuItems: <>
          <Group>
            <EditItem onClick={emailFollowUpHandler} />
          </Group>
          <Group>
            <BillingWrappedActivationToggleItem onSubmit={onSubmit} active={emailFollowUpActivated} kind="invitee_email_follow_up" doNotSlideBackOnUpdate={doNotSlideBackOnToggleUpdate} />
          </Group>
        </>
  }];
  return <>
      <LabelContainer>
        <TitleText size="x-small" level={3}>{I18n.t('publisher.event_type_editor.sections.communications.basic_notifications')}</TitleText>
        <ProTip spacingLeft={2} spacingRight={8}>
          {HtmlParser(I18n.t('publisher.event_type_editor.sections.communications.basic_notifications_tooltip_v3'))}
        </ProTip>
      </LabelContainer>
      {isNotificationCustomizationRestricted && <ContextualBanner variant="warning" icon={AlertWarningCircleIcon}>
          {I18n.t('publisher.event_types.notifications.notification_customization_restricted')}
        </ContextualBanner>}
      <StackedCards cards={cards} />
    </>;
};

require("./basic_notifications_section.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./basic_notifications_section.js");